import type { AcademyApi } from '@mathflat/domain/@entities/Academy/api'
import { DEFAULT_HIGH_SCHOOL_CURRICULUM_KEY } from '@mathflat/domain/@entities/Curriculum/constant'
import type { Entity as CurriculumEntity } from '@mathflat/domain/@entities/Curriculum/dto'
import { SCHOOL_TYPE_KO_ABBR } from '@mathflat/domain/@entities/SchoolSystem/constants'
import { Entity as StudentEntity } from '@mathflat/domain/@entities/Student/dto'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { isDevelopMode } from '@mathflat/shared/@common/utils/dev'
import DynamicRepo from '@mathflat/shared/@modules/mobx/repository/dynamicRepo.service'
import ServiceRepo from '@mathflat/shared/@modules/mobx/repository/repo.service'
import { makeAutoObservable, observable, runInAction } from 'mobx'

import { studentApi } from '../api/studentApi'
import { UserPermission } from '../models/(Permission)/UserPermission'
import { envUtils } from '../utils/envUtils'
import { CurriculumService } from './_curriculum.service'
import { ParentRepo } from './parentRepo.service'
import { localStorageService } from './storage.service'

export const serviceRepo = new ServiceRepo()
export const dynamicRepo = new DynamicRepo()

class CommonRepo {
  private _academy: AcademyApi.Response.Academy | null = null
  private _student: StudentEntity.Student | null = null
  private _curriculumCore: CurriculumService | null = null
  private _permission: UserPermission = new UserPermission({
    userRole: 'STUDENT',
    academyAuthority: 'BASIC',
    subscribeItems: null,
    isSchoolflat: envUtils.isSchool,
    restrictions: [],
  })
  // 초기 _permission 은 restrictions:[] 기본값(빈 블랙리스트=전체 허용)로 동작한다.
  // 서버 응답 전 UI 가 "전체 허용" 상태로 잠깐 보이는 깜빡임을 막기 위해
  private _isPermissionLoaded = false
  // loadInitialData 실패 사유 — 흰 화면 대신 fallback UI 노출 여부 판단용
  private _loadFailureReason: string | null = null
  readonly parent = new ParentRepo()

  constructor() {
    makeAutoObservable<
      this,
      | '_academy'
      | '_student'
      | '_curriculumCore'
      | '_permission'
      | '_isPermissionLoaded'
      | '_loadFailureReason'
    >(this, {
      _academy: observable,
      _student: observable,
      _curriculumCore: observable,
      _permission: observable,
      _isPermissionLoaded: observable,
      _loadFailureReason: observable,
    })
    if (localStorage.getItem('studentId')) {
      const studentId = localStorage.getItem('studentId') as StudentEntity.Student['id']
      this._student = new StudentEntity.Student({ id: studentId })
    }
  }

  get academyLogoUrl() {
    const DEFAULT_MATHFLAT_LOGO_URL =
      'https://mathflat-user-uploads-dev.mathflat.com/logo/Default.png'
    const DEFAULT_SCHOOLFLAT_LOGO_URL =
      'https://mathflat-frontend-asset.s3.ap-northeast-2.amazonaws.com/teacher/assets/schoolflat_logo.png'

    if (commonRepo.isSchoolflat) {
      if (this._academy?.logoUrl === DEFAULT_MATHFLAT_LOGO_URL) {
        return DEFAULT_SCHOOLFLAT_LOGO_URL
      }
    }
    return this._academy?.logoUrl
  }

  setAcademy = (academy: CommonRepo['_academy']) => {
    this._academy = academy
  }

  setStudent = (student: StudentEntity.Student) => {
    // TODO: @회준쌤 Schema validation?
    this._student = new StudentEntity.Student({
      ...this._student,
      ...student,
      appSetting: new StudentEntity.StudentAppSetting(student.appSetting),
    })
  }

  setCurriculumCore = (curriculumCore: CurriculumEntity.CurriculumCore) => {
    this._curriculumCore = new CurriculumService(curriculumCore)
  }

  setPermission(permission: UserPermission) {
    this._permission = permission
    this._isPermissionLoaded = true
  }

  get loadFailureReason() {
    return this._loadFailureReason
  }

  setLoadFailure = (reason: string) => {
    this._loadFailureReason = reason
  }

  clearLoadFailure = () => {
    this._loadFailureReason = null
  }

  get permission() {
    return this._permission
  }

  get isPermissionLoaded() {
    return this._isPermissionLoaded
  }

  get academy() {
    return this._academy
  }

  get studentId() {
    return this._student?.id
  }

  get student() {
    this._student?.schoolType
    return this._student
  }

  get studentSchoolTypeWithGrade() {
    const student = this._student
    if (student?.schoolType) {
      return SCHOOL_TYPE_KO_ABBR[student.schoolType] + student.grade
    }
    return ''
  }

  get studentAppSetting() {
    return this._student?.appSetting
  }

  get curriculumCore() {
    return this._curriculumCore
  }
  get studentDefaultCurriculumKey() {
    if (!this.student || !this.student.grade) return null
    const schoolType = this.student.schoolType
    // remote storage에 저장된 key가 없고, 학생이 고등학생이라면 `고등수학(상)`의 key를 사용한다.
    if (schoolType === 'HIGH') {
      return DEFAULT_HIGH_SCHOOL_CURRICULUM_KEY
    }

    if (schoolType === 'ELEMENTARY' || schoolType === 'MIDDLE') {
      const curriculum = [
        ...(this.curriculumCore?.getGradeSemesterElementary({ includeAll: false }) ?? []),
        ...(this.curriculumCore?.getGradeSemesterMiddle({ includeAll: false }) ?? []),
      ].find(({ revision, gradeSubject, schoolType: _schoolType }) => {
        if (!this.student?.grade) return false
        return (
          revision !== 'CURRICULUM_22' &&
          _schoolType === schoolType &&
          gradeSubject === String(this.student.grade)
        )
      })
      return curriculum?.trieKey ?? null
    }
  }

  get studentCurriculumInfo() {
    if (!this.studentDefaultCurriculumKey) return null

    const curriculumInfo = this.curriculumCore?.getItemByTrieKey(this.studentDefaultCurriculumKey)

    return curriculumInfo
  }

  get isSchoolflat() {
    return (
      envUtils.isSchool ||
      !!(this._academy?.customerType && this._academy.customerType?.includes('B2G'))
    )
  }

  get isElementarySchoolflat() {
    return this.student?.schoolType === 'ELEMENTARY' && this.isSchoolflat
  }

  get host() {
    if (this.isSchoolflat) {
      return 'schoolflat.com'
    }
    return domainProvider.TARGET_DOMAIN
  }

  get appKorName() {
    if (this.isSchoolflat) {
      return '스쿨플랫'
    }
    return '매쓰플랫'
  }

  get agencyNameInKor() {
    if (this.isSchoolflat) {
      return '학교'
    }
    return '학원'
  }

  get Ai튜터_사용가능_여부() {
    return (
      this.studentAppSetting?.개념힌트_사용 ||
      this.studentAppSetting?.풀이확인_사용 ||
      this.studentAppSetting?.풀이분석_사용
    )
  }

  get signUpFrom() {
    return this._academy?.signUpFrom || null
  }

  async loadUser() {
    const storedUserId = localStorageService.user.studentId
    if (storedUserId) {
      const { data: fetchedStudentInfo } = await studentApi.getStudentById(storedUserId)
      const {
        일반채점_채점전정답공개,
        채점후정답해설공개,
        채점전문풀동공개,
        채점후문풀동공개,
        학부모정답해설공개,
        lectureService,
        개념힌트_사용,
        풀이분석_사용,
        풀이확인_사용,
      } = await studentApi.getStudentAppSettings(storedUserId)

      runInAction(() => {
        this._student = new StudentEntity.Student({
          ...fetchedStudentInfo,
          appSetting: {
            일반채점_채점전정답공개,
            채점후정답해설공개,
            채점전문풀동공개,
            채점후문풀동공개,
            학부모정답해설공개,
            lectureService,
            개념힌트_사용,
            풀이분석_사용,
            풀이확인_사용,
          },
        })
      })
    }
  }
}

export const commonRepo = new CommonRepo()

if (isDevelopMode) globalThis._commonRepo = commonRepo
